// Premiações consolidadas — o que cada vendedor tem a receber somando todas as
// campanhas do período. Uma linha por vendedor, uma coluna por campanha.
// RH/gestão vê o grupo todo; gerente vê só o time da própria loja.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import ImagemProduto from '../components/ImagemProduto';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataBR = (d) => (d ? String(d).split('-').reverse().join('/') : '—');

// Mês corrente — mesmo padrão do backend.
const mesCorrente = () => {
  const h = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const ultimo = new Date(Date.UTC(h.getFullYear(), h.getMonth() + 1, 0)).getUTCDate();
  return { inicio: `${h.getFullYear()}-${p(h.getMonth() + 1)}-01`, fim: `${h.getFullYear()}-${p(h.getMonth() + 1)}-${p(ultimo)}` };
};

// Ordenação: asc → desc → volta pra ordem original (maior total primeiro).
// Números comparam numericamente; texto usa pt-BR.
const comparar = (a, b, dir) => {
  const va = a == null || a === '';
  const vb = b == null || b === '';
  if (va && vb) return 0;
  if (va) return dir === 'asc' ? -1 : 1;
  if (vb) return dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a;
  return dir === 'asc'
    ? String(a).localeCompare(String(b), 'pt-BR', { numeric: true })
    : String(b).localeCompare(String(a), 'pt-BR', { numeric: true });
};

const proximoSort = (prev, key) => {
  if (!prev || prev.key !== key) return { key, dir: 'asc' };
  if (prev.dir === 'asc') return { key, dir: 'desc' };
  return null;
};

function ThOrd({ chave, label, sort, setSort, alinha = 'left', style = {}, title }) {
  const ativo = sort?.key === chave;
  const icone = ativo ? (sort.dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up';
  return (
    <th style={{ textAlign: alinha, cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => setSort((prev) => proximoSort(prev, chave))}
      title={title || 'Clique para ordenar'}>
      {label}
      <i className={`bi ${icone} ms-1`} style={{ fontSize: '0.62rem', opacity: ativo ? 1 : 0.35 }}></i>
    </th>
  );
}

const ROTULO_TIPO = {
  quantidade: 'Por quantidade', ranking: 'Ranking', vendedor: 'Meta por vendedor',
  gerente: 'Meta da loja', comissao_extra: 'Comissão extra',
};

export default function Premiacoes() {
  const padrao = mesCorrente();
  const [inicio, setInicio] = useState(padrao.inicio);
  const [fim, setFim] = useState(padrao.fim);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  // Seleção de campanhas: tudo marcado por padrão, com botão de desmarcar.
  // Serve pra tirar do lançamento a campanha que ainda não encerrou.
  const [selecionadas, setSelecionadas] = useState(null); // null = ainda não inicializou
  const [busca, setBusca] = useState('');
  const [sort, setSort] = useState(null);
  // Alguns fornecedores pagam separado (ex.: Bianco e Incesa na mesma campanha),
  // entao o RH precisa ver quanto sai de cada um.
  const [porFornecedor, setPorFornecedor] = useState(false);
  // Drill-down: mesmos movimentos que o dashboard da campanha ja mostrava.
  const [detalhe, setDetalhe] = useState(null); // { vendedor, campanha, dados }
  const [carregandoDet, setCarregandoDet] = useState(false);

  const abrirDetalhe = async (vendedor, campanha) => {
    setDetalhe({ vendedor, campanha, dados: null });
    setCarregandoDet(true);
    try {
      const r = await axios.get(`${API}/premiacoes/detalhe`, {
        params: { campanha: campanha.key, codvend: vendedor.codvend },
      });
      setDetalhe({ vendedor, campanha, dados: r.data });
    } catch (e) {
      setDetalhe(null);
      Swal.fire('Erro', e.response?.data?.error || 'Erro ao buscar o detalhe', 'error');
    } finally { setCarregandoDet(false); }
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await axios.get(`${API}/premiacoes`, { params: { inicio, fim, fornecedor: porFornecedor ? 1 : 0 } });
      setDados(r.data);
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Erro ao carregar as premiações', 'error');
    } finally { setCarregando(false); }
  }, [inicio, fim, porFornecedor]);

  useEffect(() => { carregar(); }, [carregar]);

  // Período novo traz outro conjunto de campanhas — remarca todas.
  useEffect(() => {
    if (dados) setSelecionadas(new Set((dados.campanhas || []).map(c => c.key)));
  }, [dados]);

  const marcada = useCallback((key) => !selecionadas || selecionadas.has(key), [selecionadas]);
  const alternar = (key) => setSelecionadas(prev => {
    const s = new Set(prev || []);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const todasCampanhas = dados?.campanhas || [];
  const marcarTodas = () => setSelecionadas(new Set(todasCampanhas.map(c => c.key)));
  const desmarcarTodas = () => setSelecionadas(new Set());
  // Atalho pro caso que motivou isso: lançar só o que já fechou.
  const hojeIso = new Date().toISOString().slice(0, 10);
  const soEncerradas = () => setSelecionadas(new Set(todasCampanhas.filter(c => c.fim < hojeIso).map(c => c.key)));

  // Só as campanhas que pagam dinheiro entram na matriz de valores; as de prêmio
  // em espécie viram uma coluna de marcação (meta batida), fora do total.
  const colunas = useMemo(() => {
    const todas = dados?.campanhas || [];
    const filtradas = todas.filter(c => !selecionadas || selecionadas.has(c.key));
    // Campanha com quebra vira uma coluna por fornecedor; o resto segue igual.
    const dinheiro = [];
    filtradas.filter(c => c.paga_dinheiro).forEach(c => {
      if (porFornecedor && c.fornecedores?.length) {
        c.fornecedores.forEach(f => dinheiro.push({
          key: f.key, nome: f.nome, inicio: c.inicio, fim: c.fim,
          paga_dinheiro: true, deFornecedor: true, campanhaNome: c.nome,
        }));
      } else {
        dinheiro.push(c);
      }
    });
    return { dinheiro, especie: filtradas.filter(c => !c.paga_dinheiro) };
  }, [dados, selecionadas, porFornecedor]);

  const linhas = useMemo(() => {
    let vs = dados?.vendedores || [];
    const q = busca.trim().toLowerCase();
    if (q) vs = vs.filter(v => v.nome.toLowerCase().includes(q) || v.codvend.includes(q) || v.loja.toLowerCase().includes(q));
    const valorDe = (v, c) => (c.deFornecedor ? (v.porFornecedor?.[c.key] || 0) : (v.valores[c.key] || 0));
    const chaves = [...colunas.dinheiro, ...colunas.especie];
    vs = vs.filter(v => chaves.some(c => valorDe(v, c) || v.marcadores[c.key]));
    const comTotal = vs.map(v => ({
      ...v,
      totalVisivel: colunas.dinheiro.reduce((s, c) => s + valorDe(v, c), 0),
      cel: Object.fromEntries(colunas.dinheiro.map(c => [c.key, valorDe(v, c)])),
    }));
    if (!sort) return comTotal;
    // Colunas fixas usam o campo direto; coluna de campanha ordena pelo valor da célula.
    const valor = (v) => {
      if (sort.key === 'loja') return v.loja;
      if (sort.key === 'codvend') return v.codvend;
      if (sort.key === 'nome') return v.nome;
      if (sort.key === 'total') return v.totalVisivel;
      if (sort.key.startsWith('esp:')) return v.marcadores[sort.key.slice(4)] ? 1 : 0;
      return v.cel[sort.key] || 0;
    };
    return [...comTotal].sort((a, b) => comparar(valor(a), valor(b), sort.dir));
  }, [dados, busca, colunas, sort]);

  const totalGeral = linhas.reduce((s, v) => s + v.totalVisivel, 0);

  const exportar = () => {
    if (!linhas.length) { Swal.fire('Nada pra exportar', 'Nenhum vendedor no período.', 'warning'); return; }
    const num = (v) => Number(v || 0).toFixed(2).replace('.', ',');
    const txt = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Loja', 'Cód. Vendedor', 'Vendedor', ...colunas.dinheiro.map(c => `${c.nome} (R$)`), 'TOTAL (R$)'];
    const corpo = [
      [txt('Premiações consolidadas'), txt(`Período: ${dataBR(inicio)} a ${dataBR(fim)}`)].join(';'), '',
      head.map(txt).join(';'),
      ...linhas.map(v => [txt(v.loja), txt(v.codvend), txt(v.nome),
        ...colunas.dinheiro.map(c => num(v.cel[c.key] || 0)), num(v.totalVisivel)].join(';')),
      '',
      [txt('TOTAL GERAL'), '', '', ...colunas.dinheiro.map(c => num(linhas.reduce((s, v) => s + (v.cel[c.key] || 0), 0))), num(totalGeral)].join(';'),
    ].join('\r\n');
    const blob = new Blob(['﻿' + corpo], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `premiacoes-${inicio}-a-${fim}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1"><i className="bi bi-cash-coin me-2" style={{ color: '#6B2C5C' }}></i>Premiações</h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Tudo que cada vendedor tem a receber no período, somando as campanhas de premiação e as comissões extras.
            {dados?.escopo === 'loja' && <span className="badge ms-2" style={{ background: '#4A1F40', color: '#fff' }}>Só a sua loja</span>}
          </p>
        </div>
        <button className="btn btn-sm btn-outline-mkt" onClick={exportar} disabled={!linhas.length}>
          <i className="bi bi-file-earmark-spreadsheet me-1"></i>Exportar
        </button>
      </div>

      {/* Filtros */}
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 mb-1">
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              O período filtra pelo <b>encerramento</b> da campanha — traz o que fechou na janela e pode ser lançado.
            </span>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Encerrando de</label>
            <input type="date" className="form-control form-control-sm" value={inicio} onChange={e => setInicio(e.target.value)} />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>até</label>
            <input type="date" className="form-control form-control-sm" value={fim} onChange={e => setFim(e.target.value)} />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Buscar</label>
            <input className="form-control form-control-sm" placeholder="vendedor, código ou loja" value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
        </div>
      </div>

      {carregando && <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>}

      {!carregando && dados && (
        <>
          {/* Campanhas do período — clique no card pra tirar/colocar no cálculo */}
          {todasCampanhas.length > 0 && (
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                Campanhas no cálculo: <b>{colunas.dinheiro.length + colunas.especie.length}</b> de {todasCampanhas.length}
              </span>
              <button className="btn btn-sm btn-outline-secondary py-0" onClick={marcarTodas}>Marcar todas</button>
              <button className="btn btn-sm btn-outline-secondary py-0" onClick={desmarcarTodas}>Desmarcar todas</button>
              <button className="btn btn-sm btn-outline-secondary py-0" onClick={soEncerradas} title="Deixa marcadas só as campanhas que já terminaram">
                Só encerradas
              </button>
              <div className="form-check form-switch ms-2 mb-0">
                <input className="form-check-input" type="checkbox" id="sw-forn" checked={porFornecedor}
                  onChange={e => setPorFornecedor(e.target.checked)} />
                <label className="form-check-label" htmlFor="sw-forn" style={{ fontSize: '0.8rem' }}
                  title="Campanha que junta mais de um fornecedor vira uma coluna por fornecedor — útil quando cada um paga separado">
                  Separar por fornecedor
                </label>
              </div>
            </div>
          )}
          {porFornecedor && !todasCampanhas.some(c => c.fornecedores?.length) && (
            <div className="alert alert-light py-2 mb-2" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Nenhuma campanha do período junta mais de um fornecedor — não há o que separar.
            </div>
          )}
          <div className="row g-2 mb-3">
            {todasCampanhas.map(c => {
              const on = marcada(c.key);
              const encerrada = c.fim < hojeIso;
              return (
                <div className="col-6 col-md-3" key={c.key}>
                  <div className="card p-2 h-100" onClick={() => alternar(c.key)}
                    style={{
                      borderLeft: `4px solid ${c.paga_dinheiro ? '#1F5C4A' : '#8a6d00'}`,
                      cursor: 'pointer', opacity: on ? 1 : 0.45,
                      background: on ? undefined : '#f7f7f7',
                    }}
                    title={on ? 'Clique para tirar do cálculo' : 'Clique para incluir no cálculo'}>
                    <div className="d-flex align-items-start gap-2">
                      <input type="checkbox" className="form-check-input mt-1" checked={on}
                        onChange={() => alternar(c.key)} onClick={e => e.stopPropagation()} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.72rem', color: '#888' }}>
                          {ROTULO_TIPO[c.tipo] || c.tipo}
                          {!encerrada && <span className="badge ms-1" style={{ background: '#c9a227', color: '#fff', fontSize: '0.6rem' }}>em andamento</span>}
                        </div>
                        <div className="fw-bold" style={{ fontSize: '0.85rem', lineHeight: 1.2 }}>{c.nome}</div>
                        <div style={{ fontSize: '0.72rem', color: '#888' }}>{dataBR(c.inicio)} a {dataBR(c.fim)}</div>
                        {c.paga_dinheiro
                          ? <div className="fw-bold" style={{ color: '#1F5C4A' }}>R$ {fmt(c.total)}</div>
                          : <div style={{ fontSize: '0.75rem', color: '#8a6d00' }}><i className="bi bi-gift me-1"></i>{c.premio_descricao || 'Prêmio em espécie'}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {!todasCampanhas.length && <div className="col-12"><div className="alert alert-light mb-0">Nenhuma campanha no período.</div></div>}
          </div>

          {/* Matriz vendedor × campanha */}
          {linhas.length > 0 && (
            <div className="card p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                    <tr>
                      <ThOrd chave="loja" label="Loja" sort={sort} setSort={setSort} />
                      <ThOrd chave="codvend" label="Cód." sort={sort} setSort={setSort} />
                      <ThOrd chave="nome" label="Vendedor" sort={sort} setSort={setSort} />
                      {colunas.dinheiro.map(c => (
                        <ThOrd key={c.key} chave={c.key} sort={sort} setSort={setSort} alinha="right"
                          style={{ minWidth: 110 }}
                          title={c.deFornecedor
                            ? `${c.nome} — fornecedor da campanha ${c.campanhaNome}`
                            : `${c.nome} — ${dataBR(c.inicio)} a ${dataBR(c.fim)}`}
                          label={c.nome.length > 18 ? c.nome.slice(0, 18) + '…' : c.nome} />
                      ))}
                      {colunas.especie.map(c => (
                        <ThOrd key={c.key} chave={`esp:${c.key}`} sort={sort} setSort={setSort} alinha="center"
                          style={{ minWidth: 90 }} title={c.premio_descricao || ''}
                          label={c.nome.length > 14 ? c.nome.slice(0, 14) + '…' : c.nome} />
                      ))}
                      <ThOrd chave="total" label="TOTAL" sort={sort} setSort={setSort} alinha="right"
                        style={{ background: '#f6f1f4' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map(v => (
                      <tr key={v.codvend}>
                        <td style={{ fontSize: '0.78rem', color: '#666' }}>{v.loja}</td>
                        <td>{v.codvend}</td>
                        <td>{v.nome}</td>
                        {colunas.dinheiro.map(c => (
                          <td key={c.key}
                            style={{ textAlign: 'right', color: v.cel[c.key] ? undefined : '#ccc',
                              cursor: (!c.deFornecedor && v.cel[c.key]) ? 'pointer' : undefined }}
                            onClick={() => !c.deFornecedor && v.cel[c.key] && abrirDetalhe(v, c)}
                            title={c.deFornecedor ? undefined : (v.cel[c.key] ? 'Ver os movimentos' : undefined)}>
                            {v.cel[c.key]
                              ? <span style={{ textDecoration: c.deFornecedor ? undefined : 'underline dotted' }}>{fmt(v.cel[c.key])}</span>
                              : '—'}
                          </td>
                        ))}
                        {colunas.especie.map(c => (
                          <td key={c.key} style={{ textAlign: 'center' }}>
                            {v.marcadores[c.key]
                              ? <span className="badge" style={{ background: '#8a6d00', color: '#fff' }} title={c.premio_descricao || ''}>meta ✓</span>
                              : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                        ))}
                        <td className="fw-bold" style={{ textAlign: 'right', background: '#f6f1f4' }}>R$ {fmt(v.totalVisivel)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #6B2C5C' }}>
                      <th colSpan="3">TOTAL — {linhas.length} vendedor(es)</th>
                      {colunas.dinheiro.map(c => (
                        <th key={c.key} style={{ textAlign: 'right' }}>R$ {fmt(linhas.reduce((s, v) => s + (v.cel[c.key] || 0), 0))}</th>
                      ))}
                      {colunas.especie.map(c => <th key={c.key}></th>)}
                      <th style={{ textAlign: 'right', background: '#f6f1f4' }}>R$ {fmt(totalGeral)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {!linhas.length && <div className="alert alert-light">Nenhum vendedor com premiação no período.</div>}
        </>
      )}

      {/* Drill-down — mesmo modelo do dashboard da campanha de premiação */}
      {detalhe && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setDetalhe(null)}>
          <div className="card p-3" style={{ width: '95%', maxWidth: 920, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
              <div>
                <h6 className="fw-bold mb-0"><i className="bi bi-person-lines-fill me-1"></i>{detalhe.vendedor.nome}</h6>
                <small className="text-muted">{detalhe.vendedor.loja} · {detalhe.campanha.nome}</small>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setDetalhe(null)}><i className="bi bi-x-lg"></i></button>
            </div>

            {carregandoDet && <div className="text-center py-4"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>}

            {!carregandoDet && detalhe.dados && (() => {
              const d = detalhe.dados;
              const t = d.totais || {};
              const un = (d.unidade_premiada || '').toLowerCase();
              // Campanha por quantidade mostra m²/un; as outras mostram R$.
              const isQuant = d.tipo === 'quantidade';
              const val = (q, v) => (isQuant ? `${fmt(q)} ${un}` : `R$ ${fmt(v)}`);
              return (
                <>
                  <div className="d-flex gap-2 flex-wrap mb-3">
                    <div style={{ background: '#e9f3ef', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Vendido</small>
                      <span className="fw-bold">{val(t.quantVenda, t.valorVenda)}</span>
                    </div>
                    <div style={{ background: '#fdeeee', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Devolvido</small>
                      <span className="fw-bold" style={{ color: '#b53d3d' }}>{val(t.quantDevol, t.valorDevol)}</span>
                    </div>
                    {t.quantCancel > 0 && (
                      <div style={{ background: '#f0f0f0', padding: '6px 14px', borderRadius: 8 }}>
                        <small className="text-muted d-block">Cancelado <i className="bi bi-info-circle" title="Itens cancelados — não entram no prêmio"></i></small>
                        <span className="fw-bold" style={{ color: '#888' }}>{val(t.quantCancel, t.valorCancel)}</span>
                      </div>
                    )}
                    <div style={{ background: '#f6f1f4', padding: '6px 14px', borderRadius: 8 }}>
                      <small className="text-muted d-block">Líquido</small>
                      <span className="fw-bold">{val(t.quantLiquida, t.valorLiquido)}</span>
                    </div>
                    {t.premio != null && (
                      <div style={{ background: '#6B2C5C', color: '#fff', padding: '6px 14px', borderRadius: 8 }}>
                        <small style={{ opacity: 0.85 }} className="d-block">Prêmio</small>
                        <span className="fw-bold">R$ {fmt(t.premio)}</span>
                      </div>
                    )}
                  </div>

                  {!d.linhas.length && <div className="text-muted text-center py-3">Sem movimentos no período.</div>}
                  {d.linhas.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                        <thead>
                          <tr>
                            <th>Foto</th><th>Produto</th><th>Documento</th><th>Data</th><th>Tipo</th>
                            <th style={{ textAlign: 'right' }}>Quant</th><th style={{ textAlign: 'right' }}>Preço</th><th style={{ textAlign: 'right' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.linhas.map((l, i) => {
                            const isVenda = l.tipo === 'venda';
                            const isCancel = l.tipo === 'cancelamento';
                            // Devolução vermelha e negativa; cancelamento cinza (não conta).
                            const cor = isVenda ? undefined : (isCancel ? '#999' : '#b53d3d');
                            const sinal = isVenda || isCancel ? '' : '−';
                            return (
                              <tr key={i} style={{ opacity: isCancel ? 0.6 : 1 }}>
                                <td><ImagemProduto codpro={l.codpro} tamanho={44} clicavel alt={l.descricao} /></td>
                                <td><div className="fw-semibold">{l.codpro}</div><div className="text-muted" style={{ fontSize: '0.74rem' }}>{l.descricao}</div></td>
                                <td>{l.documento}</td>
                                <td>{l.data ? new Date(l.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—'}</td>
                                <td>
                                  {isVenda && <span className="badge bg-success" style={{ fontSize: '0.68rem' }}>Venda</span>}
                                  {l.tipo === 'devolucao' && <span className="badge bg-danger" style={{ fontSize: '0.68rem' }}>Devolução</span>}
                                  {isCancel && <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }} title="Cancelado — não conta no prêmio">Cancelado</span>}
                                </td>
                                <td style={{ textAlign: 'right', color: cor }}>{sinal}{fmt(l.quant)} {(l.unid || '').toLowerCase()}</td>
                                <td style={{ textAlign: 'right' }}>R$ {fmt(l.preco)}</td>
                                <td style={{ textAlign: 'right', color: cor }}>{sinal}R$ {fmt(l.valor)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
